import { classNames } from '@/lib/utils'

interface TabsProps<T extends string> {
  tabs: T[]
  active: T
  onChange: (tab: T) => void
  labels?: Record<T, string>
  /** Optional per-tab item count, rendered as "Label (count)". Tabs omitted from this map
   * (or when the prop itself is omitted) render with no count, so existing callers are
   * unaffected. */
  counts?: Partial<Record<T, number>>
  /** Optional small colored dot after a tab's label — e.g. to flag which tabs in a multi-step
   * review have been edited or still need review. Tabs omitted from this map render with no dot. */
  dots?: Partial<Record<T, 'gray' | 'green' | 'yellow'>>
}

const DOT_COLOR: Record<'gray' | 'green' | 'yellow', string> = {
  gray: 'bg-gray-300',
  green: 'bg-green-500',
  yellow: 'bg-yellow-500',
}

export function Tabs<T extends string>({ tabs, active, onChange, labels, counts, dots }: TabsProps<T>) {
  return (
    <div className="flex gap-1 rounded-lg bg-gray-100 p-1 w-fit">
      {tabs.map((tab) => {
        const count = counts?.[tab]
        const dot = dots?.[tab]
        return (
          <button
            key={tab}
            onClick={() => onChange(tab)}
            className={classNames(
              'flex items-center gap-1.5 rounded-md px-4 py-1.5 text-sm font-medium transition-colors capitalize',
              active === tab ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700',
            )}
          >
            {labels?.[tab] ?? tab}
            {count != null && <span className="ml-1 text-gray-400">({count})</span>}
            {dot && <span className={classNames('h-1.5 w-1.5 rounded-full', DOT_COLOR[dot])} />}
          </button>
        )
      })}
    </div>
  )
}
